import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const statisticsUnit8Notes: UnitNotes = {
  unitId: "unit-8",
  unitName: "Unit 8: Inference for Categorical Data (Chi-Square)",
  examWeight: "2–5%",
  focus: "Chi-Square Tests for Categorical Data",
  sections: [
    {
      id: "unit-8-overview",
      title: "Overview",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            While Unit 6 focused on categorical data for binary outcomes (Success/Failure) using z-tests, Unit 8 expands this to categorical variables with <strong>more than two categories</strong>. We use the <strong>Chi-Square (<InlineMath math="\chi^2" />) statistic</strong> to compare observed counts of categorical data with the counts we would expect to see if a specific null hypothesis were true.
          </p>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-bold text-blue-900 mb-2">Three Types of Chi-Square Tests:</h4>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li><strong>Goodness of Fit:</strong> Is a distribution of one variable different than a claimed distribution?</li>
              <li><strong>Homogeneity:</strong> Are the distributions of one variable the same across multiple populations/groups?</li>
              <li><strong>Independence:</strong> Are two variables associated within a single population?</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: "topic-8-1",
      title: "Topic 8.1: Introducing Statistics: Are My Results Unexpected?",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept of "Distance" in Categorical Data</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When analyzing categorical data, we look at the difference between what we <strong>observed</strong> in our sample and what we <strong>expected</strong> to see based on a probability model (Null Hypothesis).
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Variation:</strong> Just like with means and proportions, observed counts will vary from sample to sample due to random chance.</li>
              <li><strong>The Question:</strong> Is the difference between the Observed counts and Expected counts small (likely due to chance) or large (statistically significant)?</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Chi-Square Statistic (<InlineMath math="\chi^2" />)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To quantify the "distance" between observed and expected data, we use the Chi-Square statistic.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="\chi^2 = \sum \frac{(\text{Observed} - \text{Expected})^2}{\text{Expected}}" />
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Observed (<InlineMath math="O" />):</strong> The actual count from the sample data.</li>
              <li><strong>Expected (<InlineMath math="E" />):</strong> The count predicted by the null hypothesis.</li>
              <li><strong>Squaring:</strong> We square the difference so positive and negative deviations don't cancel each other out.</li>
              <li><strong>Dividing by Expected:</strong> This standardizes the difference. A difference of 5 is huge if we expected 10, but negligible if we expected 10,000.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Properties of the Chi-Square Distribution</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Values:</strong> Always positive (<InlineMath math="\ge 0" />).</li>
              <li><strong>Shape:</strong> Skewed to the right. As Degrees of Freedom (<InlineMath math="df" />) increase, the curve becomes flatter and more symmetric.</li>
              <li><strong>Area:</strong> The P-value is always the area in the <strong>right tail</strong>. (A <InlineMath math="\chi^2" /> of 0 means perfect agreement; a large <InlineMath math="\chi^2" /> means outcomes are far from expectation).</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-2",
      title: "Topic 8.2: Setting Up a Chi-Square Goodness of Fit Test",
      content: (
        <div className="space-y-6">
          <KeyTerm 
            term="Goodness of Fit (GOF) Test" 
            definition="A test that checks to see if a sample distribution of one categorical variable matches a claimed population distribution."
          />

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Stating Hypotheses</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Hypotheses for Chi-Square tests are usually written in words.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900"><InlineMath math="H_0" /> (Null)</h4>
                <p className="text-sm text-slate-700 mt-1">The specified distribution of the categorical variable is correct.</p>
                <p className="text-sm text-slate-600 mt-1"><em>Example:</em> "The distribution of M&M colors is 20% red, 20% blue..."</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900"><InlineMath math="H_a" /> (Alternative)</h4>
                <p className="text-sm text-slate-700 mt-1">The specified distribution is <strong>not</strong> correct.</p>
                <p className="text-sm text-slate-600 mt-1">(It does not have to be totally different, just different in at least one category).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculating Expected Counts</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              For a GOF test, the Null Hypothesis usually provides specific proportions (<InlineMath math="p_i" />) for each category.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="\text{Expected Count} = n \times p_i" />
              <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                <li><InlineMath math="n" /> = Total sample size</li>
                <li><InlineMath math="p_i" /> = Hypothesized proportion for that category</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions for Inference</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Random:</strong> The data must come from a random sample or randomized experiment.</li>
              <li><strong>10% Condition:</strong> If sampling without replacement, <InlineMath math="n < 10\%" /> of the population.</li>
              <li><strong>Large Counts:</strong> <strong>All</strong> Expected counts must be <InlineMath math="\ge 5" />.</li>
            </ol>
            <ImportantNote>
              Do not check observed counts. Do not check if <InlineMath math="n \ge 30" />. You must strictly check the <strong>expected</strong> counts.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Degrees of Freedom (GOF)</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="df = \text{Number of Categories} - 1" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-3",
      title: "Topic 8.3: Carrying Out a Chi-Square Test for Goodness of Fit",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculating the Test Statistic</h3>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 mb-4">
              <li>Find the contribution of each category: <InlineMath math="\frac{(O-E)^2}{E}" /></li>
              <li>Sum these contributions to get <InlineMath math="\chi^2" /></li>
            </ol>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-800 mb-2">Example Calculation:</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li>Category A: Observed 10, Expected 20. Contribution: <InlineMath math="\frac{(10-20)^2}{20} = \frac{100}{20} = 5" /></li>
                <li>Category B: Observed 30, Expected 20. Contribution: <InlineMath math="\frac{(30-20)^2}{20} = \frac{100}{20} = 5" /></li>
                <li><InlineMath math="\chi^2 = 5 + 5 = 10" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding the P-Value</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Use the Chi-Square CDF on a calculator (lower bound = <InlineMath math="\chi^2" />, upper bound = <InlineMath math="\infty" />, <InlineMath math="df = \text{categories} - 1" />) or a Chi-Square table.</li>
              <li><strong>Interpretation:</strong> The probability of observing a <InlineMath math="\chi^2" /> statistic as large or larger than the one calculated, assuming the null hypothesis is true.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Follow-up Analysis (Components)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If the test yields a significant result (Reject <InlineMath math="H_0" />), examine the individual components:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="\text{Component} = \frac{(\text{Observed} - \text{Expected})^2}{\text{Expected}}" />
            </div>
            <p className="text-slate-700">
              Identify which category contributed the most to the <InlineMath math="\chi^2" /> sum. This tells you <em>which</em> category differed most from the expectation.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-4",
      title: "Topic 8.4: Expected Counts in Two-Way Tables",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When dealing with <strong>two categorical variables</strong>, data is organized in a two-way table (contingency table) with <InlineMath math="r" /> rows and <InlineMath math="c" /> columns.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Logic of Independence</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If two variables are independent, the distribution of one variable should not change based on the level of the other variable.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="P(A \cap B) = P(A) \times P(B)" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Formula for Expected Counts</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To perform tests on two-way tables, we calculate what the counts <em>would</em> be if the null hypothesis (independence/homogeneity) were true.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="\text{Expected Count} = \frac{(\text{Row Total}) \times (\text{Column Total})}{\text{Table Total}}" />
            </div>
            <ImportantNote>
              <strong>AP Exam Tip:</strong> You must show the calculation for at least a few cells to demonstrate understanding, or list them in a matrix labeled "Expected Counts."
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-5",
      title: "Topic 8.5: Setting Up a Chi-Square Test for Homogeneity or Independence",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            These two tests look mathematically identical (same formula, same <InlineMath math="df" />, same expected counts), but they differ in <strong>study design</strong> and <strong>hypotheses phrasing</strong>.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Chi-Square Test for Homogeneity</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <p className="text-sm text-slate-700"><strong>Study Design:</strong> You sample from <strong>multiple populations</strong> (e.g., 100 Americans, 100 Canadians, 100 Mexicans) OR you have a randomized experiment with <strong>multiple treatment groups</strong>.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Goal:</strong> Compare the distribution of <em>one</em> categorical variable across the different groups.</p>
            </div>
            <div className="space-y-2">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm"><strong><InlineMath math="H_0" />:</strong> There is <strong>no difference</strong> in the distribution of [variable] for the [populations/treatments].</p>
              </div>
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <p className="text-sm"><strong><InlineMath math="H_a" />:</strong> There <strong>is a difference</strong> in the distribution of [variable] for the [populations/treatments].</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Chi-Square Test for Independence</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
              <p className="text-sm text-slate-700"><strong>Study Design:</strong> You take a <strong>single random sample</strong> from <strong>one population</strong> and classify each individual by <strong>two categorical variables</strong>.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Goal:</strong> Determine if there is an association between the two variables.</p>
            </div>
            <div className="space-y-2">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm"><strong><InlineMath math="H_0" />:</strong> There is <strong>no association</strong> between [Var 1] and [Var 2] in the population (they are independent).</p>
              </div>
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <p className="text-sm"><strong><InlineMath math="H_a" />:</strong> There <strong>is an association</strong> between [Var 1] and [Var 2] in the population (they are not independent).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions (Same for both)</h3>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li><strong>Random:</strong> SRS or Randomized Experiment.</li>
              <li><strong>10% Condition:</strong> Sample size <InlineMath math="n < 10\%" /> of population (if sampling without replacement).</li>
              <li><strong>Large Counts:</strong> All <strong>Expected Counts</strong> <InlineMath math="\ge 5" />.</li>
            </ol>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-6",
      title: "Topic 8.6: Carrying Out a Chi-Square Test for Homogeneity or Independence",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Degrees of Freedom</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              For a two-way table with <InlineMath math="r" /> rows and <InlineMath math="c" /> columns:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="df = (r - 1)(c - 1)" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculating the Statistic</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Sum the components over all cells in the two-way table:</p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="\chi^2 = \sum_{\text{all cells}} \frac{(\text{Observed} - \text{Expected})^2}{\text{Expected}}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conclusion Structure</h3>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 mb-4">
              <li>Compare P-value to <InlineMath math="\alpha" /> (significance level, usually 0.05).</li>
              <li><strong>If P-value <InlineMath math="< \alpha" />:</strong> Reject <InlineMath math="H_0" />. There is convincing evidence of a difference in distributions (Homogeneity) or an association between variables (Independence).</li>
              <li><strong>If P-value <InlineMath math="> \alpha" />:</strong> Fail to reject <InlineMath math="H_0" />. There is not convincing evidence of a difference/association.</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Computer Output Interpretation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              AP Statistics exams often provide computer output for Chi-Square tests.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Locate the "Chi-Sq" or "Pearson Chi-Square" value.</li>
              <li>Locate the "DF" (Degrees of Freedom).</li>
              <li>Locate the "P-Value."</li>
              <li>Sometimes output lists the contribution of each cell. The largest contribution indicates where the observed data deviated most from the null hypothesis.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-8-7",
      title: "Topic 8.7: Skills Focus: Selecting an Appropriate Inference Procedure",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Distinguishing the Three Chi-Square Tests</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-slate-300 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-2 text-left">Feature</th>
                    <th className="border border-slate-300 p-2 text-left">Goodness of Fit (GOF)</th>
                    <th className="border border-slate-300 p-2 text-left">Homogeneity</th>
                    <th className="border border-slate-300 p-2 text-left">Independence</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-300 p-2 font-semibold">Number of Samples</td>
                    <td className="border border-slate-300 p-2"><strong>One</strong> sample</td>
                    <td className="border border-slate-300 p-2"><strong>Multiple</strong> samples (groups)</td>
                    <td className="border border-slate-300 p-2"><strong>One</strong> sample</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 p-2 font-semibold">Variables</td>
                    <td className="border border-slate-300 p-2"><strong>One</strong> categorical variable</td>
                    <td className="border border-slate-300 p-2"><strong>One</strong> variable measured across groups</td>
                    <td className="border border-slate-300 p-2"><strong>Two</strong> categorical variables</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 p-2 font-semibold">Comparison</td>
                    <td className="border border-slate-300 p-2">Sample vs. Known standard</td>
                    <td className="border border-slate-300 p-2">Group A vs. Group B</td>
                    <td className="border border-slate-300 p-2">Var 1 and Var 2 relationship</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 p-2 font-semibold">Data Format</td>
                    <td className="border border-slate-300 p-2">List of counts</td>
                    <td className="border border-slate-300 p-2">Two-way Table</td>
                    <td className="border border-slate-300 p-2">Two-way Table</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 p-2 font-semibold">Degrees of Freedom</td>
                    <td className="border border-slate-300 p-2">Categories − 1</td>
                    <td className="border border-slate-300 p-2">(r−1)(c−1)</td>
                    <td className="border border-slate-300 p-2">(r−1)(c−1)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Distinguishing Categorical vs. Quantitative Inference</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Means (<InlineMath math="t" />-procedures):</strong> Used for <strong>Quantitative</strong> data (numbers with units, like height, weight).</li>
              <li><strong>Proportions (<InlineMath math="z" />-procedures):</strong> Used for <strong>Categorical</strong> data with <strong>two</strong> categories (Success/Failure).</li>
              <li><strong>Chi-Square:</strong> Used for <strong>Categorical</strong> data with <strong>three or more</strong> categories.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Common Student Errors</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Error: Writing hypotheses using symbols/parameters</h4>
                <p className="text-sm text-green-700 mt-1"><strong>Correction:</strong> Always write Chi-Square hypotheses in words/context.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Error: Checking observed counts for Large Counts condition</h4>
                <p className="text-sm text-green-700 mt-1"><strong>Correction:</strong> Always check <strong>Expected</strong> counts <InlineMath math="\ge 5" />.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Error: Stating "We accept the null" or "We proved variables are independent"</h4>
                <p className="text-sm text-green-700 mt-1"><strong>Correction:</strong> "We fail to reject the null; there is insufficient evidence of an association."</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default statisticsUnit8Notes;
